import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import {
  ApiConfiguration,
  FinancialSupportCaseCreateDto,
  FinancialSupportCaseResultDto,
  FinancialSupportCasesService,
  FinancialSupportDemoCompanySummaryDto,
  FinancialSupportDemoGrantBenefitDto,
  FinancialSupportDemoService,
  SgmsPolicyFundMatchDto,
} from '@hello-unicorn/api-client';
import { firstValueFrom } from 'rxjs';
import {
  createFinancialSupportDraft,
  FinancialSupportDraft,
} from '../app/models/financial-support.model';
import { defaultPolicyFundMatches } from '../app/data/financial-support-experience.data';
import { FinancialSupportExperienceService } from '../app/services/financial-support-experience.service';

type FinancialSupportSession = {
  caseId: string;
  accessToken: string;
};

type SourceState = 'IDLE' | 'LOADING' | 'READY' | 'FAILED';

export type FinancialSupportRunwayResult = {
  runwayMonths: number;
  monthlyRevenue: number;
  netMonthlyBurn: number;
};

const SESSION_KEY = 'financial_support_session';
const DRAFT_KEY = 'sgms_financial_support_draft';

const isFinancialSupportSession = (
  value: unknown
): value is FinancialSupportSession =>
  typeof value === 'object' &&
  value !== null &&
  'caseId' in value &&
  typeof value.caseId === 'string' &&
  'accessToken' in value &&
  typeof value.accessToken === 'string';

const purposeFromLabel = (
  purpose: string
): FinancialSupportCaseCreateDto['purpose'] => {
  if (purpose.includes('시설')) return 'FACILITY';
  if (purpose.includes('확장')) return 'BUSINESS_EXPANSION';
  if (purpose.includes('고금리') || purpose.includes('대환')) {
    return 'HIGH_INTEREST_REFINANCE';
  }
  if (purpose.includes('경영애로')) return 'MANAGEMENT_DIFFICULTY';
  return 'WORKING_CAPITAL';
};

const businessAgeYears = (openedAt: string) => {
  const opened = new Date(`${openedAt}T00:00:00Z`);
  const reference = new Date('2026-08-23T00:00:00Z');
  return Math.round(
    (((reference.getUTCFullYear() - opened.getUTCFullYear()) * 12 +
      reference.getUTCMonth() -
      opened.getUTCMonth() -
      (reference.getUTCDate() < opened.getUTCDate() ? 1 : 0)) /
      12) *
      10
  ) / 10;
};

const openedAtFromLegacyAge = (age: number) =>
  `${Math.max(1900, 2026 - Math.floor(age))}-08-23`;

const isReadyResult = (result: FinancialSupportCaseResultDto | null) =>
  result?.states.similarCompany === 'READY' &&
  result.states.policyMatch === 'READY' &&
  result.states.simulation === 'READY' &&
  result.states.decision === 'READY';

const randomInt = (min: number, max: number, step = 1) =>
  min + Math.floor(Math.random() * (Math.floor((max - min) / step) + 1)) * step;

@Injectable({ providedIn: 'root' })
export class FinancialSupportStore {
  private readonly api = inject(FinancialSupportCasesService);
  private readonly demoApi = inject(FinancialSupportDemoService);
  private readonly http = inject(HttpClient);
  private readonly apiConfiguration = inject(ApiConfiguration);
  private readonly experience = inject(FinancialSupportExperienceService);
  private analysisPromise: Promise<FinancialSupportCaseResultDto> | null = null;
  private readonly usingLocalPolicyFunds = signal(false);

  readonly draft = signal<FinancialSupportDraft>(createFinancialSupportDraft());
  readonly currentStep = signal(1);
  readonly demoCompanies = signal<FinancialSupportDemoCompanySummaryDto[]>([]);
  readonly selectedDemoCompanyId = signal<string | null>(null);
  readonly sourceStates = signal<Record<'company' | 'credit' | 'loans' | 'grants', SourceState>>({
    company: 'IDLE',
    credit: 'IDLE',
    loans: 'IDLE',
    grants: 'IDLE',
  });
  readonly selectedGrantBenefits = signal<FinancialSupportDemoGrantBenefitDto[]>([]);
  readonly matches = signal<SgmsPolicyFundMatchDto[]>([]);
  readonly selectedPolicyFund = signal<SgmsPolicyFundMatchDto | null>(null);
  readonly selectedAmount = signal<number | null>(null);
  readonly caseId = signal<string | null>(null);
  readonly accessToken = signal<string | null>(null);
  readonly result = signal<FinancialSupportCaseResultDto | null>(null);
  readonly runwayAnalysis = signal<FinancialSupportRunwayResult | null>(null);
  readonly isSubmitting = signal(false);
  readonly effectiveResult = computed(() =>
    isReadyResult(this.result()) ? this.result() : this.experience.result()
  );

  constructor() {
    this.restoreDraft();
    this.restoreSession();
  }

  nextStep() {
    this.currentStep.update((step) => Math.min(5, step + 1));
  }

  previousStep() {
    this.currentStep.update((step) => Math.max(1, step - 1));
  }

  updateDraft(patch: Partial<FinancialSupportDraft>) {
    this.draft.update((draft) => ({ ...draft, ...patch }));
    this.persistDraft();
  }

  async loadDemoCompanies() {
    const companies = await firstValueFrom(
      this.demoApi.financialSupportDemoControllerFindAll()
    );
    this.demoCompanies.set(companies);
    return companies;
  }

  async applyDemoCompany(id: string) {
    this.setSourceState('company', 'LOADING');
    try {
      const company = await firstValueFrom(
        this.demoApi.financialSupportDemoControllerFindOne({ id })
      );
      this.selectedDemoCompanyId.set(id);
      this.updateDraft({
        ...company,
        businessForm:
          company.businessForm as FinancialSupportCaseCreateDto['businessForm'],
        taxType: company.taxType as FinancialSupportCaseCreateDto['taxType'],
        occupancy:
          company.occupancy as FinancialSupportCaseCreateDto['occupancy'],
        normalizedOwnership:
          company.normalizedOwnership as FinancialSupportCaseCreateDto['normalizedOwnership'],
        representativeGender:
          company.representativeGender as FinancialSupportCaseCreateDto['representativeGender'],
        purpose: purposeFromLabel(company.purpose),
        businessAgeYears: businessAgeYears(company.openedAt),
        annualRevenue: company.revenue2025,
        employeeCount: company.employeeCount2026,
        monthlyEssentialExpense: company.monthlyEssentialExpense2026,
      });
      this.setSourceState('company', 'READY');
      return company;
    } catch (error) {
      this.setSourceState('company', 'FAILED');
      throw error;
    }
  }

  connectCredit() {
    this.setSourceState('credit', 'LOADING');
    const credit = {
      cbScore: randomInt(620, 880),
      sohoScore: randomInt(560, 820),
    };
    this.updateDraft(credit);
    this.setSourceState('credit', 'READY');
    return Promise.resolve(credit);
  }

  connectLoans() {
    this.setSourceState('loans', 'LOADING');
    const loanCount = randomInt(0, 5);
    const delinquentNow = loanCount > 0 && Math.random() < 0.1;
    const cumulativeDelinquencyCount = loanCount
      ? delinquentNow
        ? randomInt(1, 8)
        : randomInt(0, 4)
      : 0;
    const delinquencyBands = [0, 1, 11, 31, 61, 91] as const;
    const loans = {
      loanCount,
      monthsSinceLastLoan: loanCount ? randomInt(3, 36) : 0,
      currentFinancialBalance: loanCount
        ? randomInt(10_000_000, 250_000_000, 10_000_000)
        : 0,
      monthlyRepayment: loanCount ? randomInt(300_000, 7_000_000, 100_000) : 0,
      financialInstitutionCount: loanCount
        ? randomInt(1, Math.min(loanCount, 4))
        : 0,
      delinquentNow,
      cumulativeDelinquencyCount,
      maxDelinquencyDays:
        delinquencyBands[
          cumulativeDelinquencyCount
            ? randomInt(1, delinquencyBands.length - 1)
            : 0
        ],
    };
    this.updateDraft(loans);
    this.setSourceState('loans', 'READY');
    return Promise.resolve(loans);
  }

  async loadGrantBenefits() {
    this.setSourceState('grants', 'LOADING');
    try {
      const companies = this.demoCompanies().length
        ? this.demoCompanies()
        : await this.loadDemoCompanies();
      const id =
        this.selectedDemoCompanyId() ??
        companies[Math.floor(Math.random() * companies.length)]?.id;
      if (!id) throw new Error('지원사업 정보를 불러오지 못했습니다.');
      const grants = await firstValueFrom(
        this.demoApi.financialSupportDemoControllerFindGrantBenefits({ id })
      );
      this.setSelectedGrantBenefits(grants);
      this.setSourceState('grants', 'READY');
      return grants;
    } catch (error) {
      this.setSourceState('grants', 'FAILED');
      throw error;
    }
  }

  setSelectedGrantBenefits(grants: readonly FinancialSupportDemoGrantBenefitDto[]) {
    this.selectedGrantBenefits.set([...grants]);
    this.updateDraft({
      grantBenefits: grants.map((grant) => ({
        externalKey: grant.id,
        year: grant.year,
        period: grant.period,
        totalBudget: grant.totalBudget,
        executedAmount: grant.executedAmount,
      })),
    });
  }

  async createCase(draft: FinancialSupportDraft = this.draft()) {
    this.draft.set({ ...draft });
    this.persistDraft();
    this.isSubmitting.set(true);
    try {
      const response = await firstValueFrom(
        this.api.financialSupportCaseControllerCreate({
          body: this.toCreateBody(draft),
        })
      );
      this.caseId.set(response.caseId);
      this.accessToken.set(response.accessToken);
      this.experience.prepare(draft);
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({
          caseId: response.caseId,
          accessToken: response.accessToken,
        })
      );
      return response;
    } finally {
      this.isSubmitting.set(false);
    }
  }

  async createCaseAndMatch() {
    try {
      await this.createCase();
      return this.matchPolicyFunds();
    } catch {
      this.experience.prepare(this.draft());
      this.usingLocalPolicyFunds.set(true);
      this.matches.set(defaultPolicyFundMatches);
      return defaultPolicyFundMatches;
    }
  }

  async matchPolicyFunds() {
    try {
      const session = this.requireSession();
      const matches = await firstValueFrom(
        this.api.financialSupportCaseControllerMatchPolicyFunds({
          id: session.caseId,
          'X-Financial-Support-Token': session.accessToken,
        })
      );
      const resolvedMatches = matches.length
        ? matches
        : defaultPolicyFundMatches;
      this.usingLocalPolicyFunds.set(!matches.length);
      this.matches.set(resolvedMatches);
      return resolvedMatches;
    } catch {
      this.usingLocalPolicyFunds.set(true);
      this.matches.set(defaultPolicyFundMatches);
      return defaultPolicyFundMatches;
    }
  }

  async loadResult() {
    const caseId = this.caseId();
    const accessToken = this.accessToken();
    if (!caseId || !accessToken) return null;

    const result = await firstValueFrom(
      this.api.financialSupportCaseControllerFindResult({
        id: caseId,
        'X-Financial-Support-Token': accessToken,
      })
    );
    this.result.set(result);
    return result;
  }

  async selectPolicyFund(
    policyFundRevisionId: string,
    selectedAmount: number
  ) {
    const selected =
      this.matches().find(
        (match) => match.policyFundRevisionId === policyFundRevisionId
      ) ?? null;
    this.selectedPolicyFund.set(selected);
    this.selectedAmount.set(selectedAmount);
    this.experience.selectPolicyFund(policyFundRevisionId, selectedAmount);
    if (this.usingLocalPolicyFunds()) return null;
    try {
      const session = this.requireSession();
      return await firstValueFrom(
        this.api.financialSupportCaseControllerSelect({
          id: session.caseId,
          'X-Financial-Support-Token': session.accessToken,
          body: { policyFundRevisionId, selectedAmount },
        })
      );
    } catch {
      this.usingLocalPolicyFunds.set(true);
      return null;
    }
  }

  async runAnalysis() {
    const ready = this.result();
    if (isReadyResult(ready)) return ready;
    if (this.usingLocalPolicyFunds()) return this.useExperienceResult();
    if (this.analysisPromise) return this.analysisPromise;
    try {
      const session = this.requireSession();
      this.analysisPromise = firstValueFrom(
        this.api.financialSupportCaseControllerRunAnalysis({
          id: session.caseId,
          'X-Financial-Support-Token': session.accessToken,
        })
      );
      const result = await this.analysisPromise;
      this.result.set(result);
      return result;
    } catch {
      this.usingLocalPolicyFunds.set(true);
      return this.useExperienceResult();
    } finally {
      this.analysisPromise = null;
    }
  }

  async analyzeRunway() {
    const session = this.requireSession();
    const result = await firstValueFrom(
      this.http.post<FinancialSupportRunwayResult>(
        `${this.apiConfiguration.rootUrl}/api/financial-support/cases/${session.caseId}/runway-analysis`,
        {},
        {
          headers: {
            'X-Financial-Support-Token': session.accessToken,
          },
        }
      )
    );
    this.runwayAnalysis.set(result);
    return result;
  }

  restart() {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(DRAFT_KEY);
    this.draft.set(createFinancialSupportDraft());
    this.currentStep.set(1);
    this.selectedDemoCompanyId.set(null);
    this.selectedGrantBenefits.set([]);
    this.matches.set([]);
    this.selectedPolicyFund.set(null);
    this.selectedAmount.set(null);
    this.caseId.set(null);
    this.accessToken.set(null);
    this.result.set(null);
    this.runwayAnalysis.set(null);
    this.usingLocalPolicyFunds.set(false);
  }

  private toCreateBody(draft: FinancialSupportDraft): FinancialSupportCaseCreateDto {
    const revenue2025 = draft.revenue2025 ?? draft.annualRevenue;
    const employeeCount2026 = draft.employeeCount2026 ?? draft.employeeCount;
    const monthlyExpense2026 =
      draft.monthlyEssentialExpense2026 ?? draft.monthlyEssentialExpense;
    return {
      companyName: draft.companyName || '입력 기업',
      businessForm: draft.businessForm || 'SOLE_PROPRIETOR',
      industryName: draft.industryName,
      industryCode: draft.industryCode || '00000',
      taxType: draft.taxType || 'GENERAL',
      openedAt:
        draft.openedAt || openedAtFromLegacyAge(draft.businessAgeYears),
      businessRegion: draft.businessRegion || '미입력',
      businessDistrict: draft.businessDistrict || '미입력',
      franchise: draft.franchise ?? false,
      occupancy: draft.occupancy || 'MONTHLY_RENT',
      normalizedOwnership: draft.normalizedOwnership || 'LEASED',
      representativeBirthYear: draft.representativeBirthYear ?? 1980,
      representativeGender: draft.representativeGender || 'MALE',
      representativeHomeRegion: draft.representativeHomeRegion || '미입력',
      pastStartupCount: draft.pastStartupCount ?? 0,
      pastClosureCount: draft.pastClosureCount ?? 0,
      sameIndustryCareerYears: draft.sameIndustryCareerYears ?? 0,
      revenue2023: draft.revenue2023 ?? revenue2025,
      revenue2024: draft.revenue2024 ?? revenue2025,
      revenue2025,
      revenue2025SamePeriod: draft.revenue2025SamePeriod ?? revenue2025,
      revenue2026Ytd: draft.revenue2026Ytd ?? revenue2025,
      operatingProfit2025: draft.operatingProfit2025,
      employeeCount2025: draft.employeeCount2025 ?? employeeCount2026,
      employeeCount2026,
      availableCash: draft.availableCash,
      monthlyEssentialExpense2025:
        draft.monthlyEssentialExpense2025 ?? monthlyExpense2026,
      monthlyEssentialExpense2026: monthlyExpense2026,
      monthlyRent2026: draft.monthlyRent2026 ?? 0,
      receivables: draft.receivables ?? 0,
      payablesWithin30Days: draft.payablesWithin30Days ?? 0,
      taxDue: draft.taxDue ?? false,
      insuranceDue: draft.insuranceDue ?? false,
      cbScore: draft.cbScore ?? 0,
      sohoScore: draft.sohoScore ?? 0,
      loanCount: draft.loanCount ?? 0,
      monthsSinceLastLoan: draft.monthsSinceLastLoan,
      delinquentNow: draft.delinquentNow ?? false,
      currentFinancialBalance: draft.currentFinancialBalance ?? 0,
      monthlyRepayment: draft.monthlyRepayment ?? 0,
      financialInstitutionCount: draft.financialInstitutionCount ?? 0,
      cumulativeDelinquencyCount: draft.cumulativeDelinquencyCount ?? 0,
      maxDelinquencyDays: draft.maxDelinquencyDays ?? 0,
      purpose: draft.purpose || 'WORKING_CAPITAL',
      requestedAmount: draft.requestedAmount,
      grantBenefits: draft.grantBenefits ?? [],
    };
  }

  private requireSession() {
    const caseId = this.caseId();
    const accessToken = this.accessToken();
    if (!caseId || !accessToken) throw new Error('금융지원 세션이 없습니다.');
    return { caseId, accessToken };
  }

  private useExperienceResult() {
    const result =
      this.experience.result() ?? this.experience.prepare(this.draft());
    this.result.set(result);
    return result;
  }

  private setSourceState(
    key: 'company' | 'credit' | 'loans' | 'grants',
    state: SourceState
  ) {
    this.sourceStates.update((states) => ({ ...states, [key]: state }));
  }

  private persistDraft() {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(this.draft()));
  }

  private restoreDraft() {
    const saved = sessionStorage.getItem(DRAFT_KEY);
    if (!saved) return;
    try {
      this.draft.set({ ...createFinancialSupportDraft(), ...JSON.parse(saved) });
    } catch {
      sessionStorage.removeItem(DRAFT_KEY);
    }
  }

  private restoreSession() {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (!saved) return;
    try {
      const session: unknown = JSON.parse(saved);
      if (!isFinancialSupportSession(session)) throw new Error('Invalid session');
      this.caseId.set(session.caseId);
      this.accessToken.set(session.accessToken);
    } catch {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }
}
